import { Client } from "@stomp/stompjs";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SockJS from "sockjs-client";

const NotificationContext = createContext();
const API_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://merchantapex.fairsurepay.com"
).replace(/\/+$/, "");
const unwrap = (body) => body?.data ?? body;
const normalize = (item) => {
  let metadata = item.metadata;
  if (typeof metadata === "string") {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      /* Preserve non-JSON metadata. */
    }
  }
  return { ...item, metadata, read: Boolean(item.read) };
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { token, loading: authLoading, sessionKey, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const retryNotifications = useCallback(
    () => setConnectionAttempt((attempt) => attempt + 1),
    []
  );
  const session = useRef(null);
  const items = useRef([]);
  const replaceItems = useCallback((next) => {
    items.current = next;
    setNotifications(next);
  }, []);
  const clearNotifications = useCallback(() => {
    replaceItems([]);
    setUnreadCount(0);
    setError(null);
    setLoading(false);
  }, [replaceItems]);
  const handleError = useCallback(
    (err, active, fallback) => {
      if (session.current !== active || active.controller.signal.aborted)
        return;
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      setError(
        err?.response?.status === 403
          ? "Access to notifications was denied."
          : err?.response?.data?.message || fallback
      );
    },
    [logout]
  );

  const fetchNotifications = useCallback(
    async ({ silent = false } = {}) => {
      const active = session.current;
      if (!active || active.controller.signal.aborted) return [];
      if (!silent) setLoading(true);
      const revision = active.revision;
      try {
        const [history, count] = await Promise.all([
          axios.get(`${API_BASE}/api/v1/notifications`, {
            ...active.options,
            params: { page: 0, size: 20 },
          }),
          axios.get(
            `${API_BASE}/api/v1/notifications/unread-count`,
            active.options
          ),
        ]);
        if (session.current !== active || active.controller.signal.aborted)
          return [];
        // Do not overwrite a push or read decision that arrived during this snapshot.
        if (revision === active.revision) {
          const payload = unwrap(history.data);
          const next = (
            Array.isArray(payload) ? payload : payload?.content || []
          ).map(normalize);
          replaceItems(next);
          const total = unwrap(count.data);
          setUnreadCount(
            Math.max(
              0,
              Number(total?.unreadCount ?? total?.count ?? total) || 0
            )
          );
        }
        setError(null);
        return items.current;
      } catch (err) {
        handleError(
          err,
          active,
          "Failed to fetch notifications. Please retry."
        );
        return [];
      } finally {
        if (session.current === active) setLoading(false);
      }
    },
    [handleError, replaceItems]
  );

  useEffect(() => {
    clearNotifications();
    setConnectionStatus("disconnected");
    if (authLoading || !token) return undefined;
    const controller = new AbortController();
    const active = {
      controller,
      revision: 0,
      pending: new Set(),
      options: {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      },
    };
    session.current = active;
    let client;
    let connectedBefore = false;
    const connect = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/api/v1/notifications/ws-config`,
          active.options
        );
        if (session.current !== active || controller.signal.aborted) return;
        const config = unwrap(response.data);
        if (!config?.endpoint || !config?.subscribeTopic)
          throw new Error("Missing notification connection configuration");
        setConnectionStatus("connecting");
        client = new Client({
          webSocketFactory: () =>
            new SockJS(`${API_BASE}/${config.endpoint.replace(/^\/+/, "")}`),
          connectHeaders: { Authorization: `Bearer ${token}` },
          reconnectDelay: 5000,
          onConnect: () => {
            if (session.current !== active || controller.signal.aborted) return;
            setConnectionStatus("connected");
            setError(null);
            client.subscribe(config.subscribeTopic, (frame) => {
              if (session.current !== active || controller.signal.aborted)
                return;
              try {
                const notification = normalize(JSON.parse(frame.body));
                if (!notification.id) return;
                const previous = items.current.find(
                  (item) => item.id === notification.id
                );
                if (previous?.read) notification.read = true;
                active.revision += 1;
                replaceItems([
                  notification,
                  ...items.current.filter(
                    (item) => item.id !== notification.id
                  ),
                ]);
                if (!previous && !notification.read)
                  setUnreadCount((count) => count + 1);
              } catch {
                setError("A notification could not be displayed.");
              }
            });
            if (connectedBefore) fetchNotifications({ silent: true });
            connectedBefore = true;
          },
          onWebSocketClose: () => {
            if (session.current === active && !controller.signal.aborted)
              setConnectionStatus("reconnecting");
          },
          onStompError: () => {
            if (session.current === active && !controller.signal.aborted)
              setError(
                "Live notifications are unavailable. Please sign in again if the problem persists."
              );
          },
        });
        client.activate();
      } catch (err) {
        handleError(
          err,
          active,
          "Unable to connect live notifications. Please sign in again to reconnect."
        );
      }
    };
    // Load the initial snapshot before subscribing so it cannot replace a live event.
    fetchNotifications().then(() => {
      if (!controller.signal.aborted) connect();
    });
    return () => {
      controller.abort();
      if (session.current === active) session.current = null;
      if (client) void client.deactivate();
    };
  }, [
    connectionAttempt,
    token,
    authLoading,
    sessionKey,
    clearNotifications,
    fetchNotifications,
    handleError,
    replaceItems,
  ]);

  const markNotificationAsRead = useCallback(
    async (id) => {
      const active = session.current;
      if (!active || !id || active.pending.has(id)) return false;
      if (items.current.find((item) => item.id === id)?.read) return true;
      active.pending.add(id);
      try {
        await axios.put(
          `${API_BASE}/api/v1/notifications/${encodeURIComponent(id)}/read`,
          {},
          active.options
        );
        if (session.current !== active || active.controller.signal.aborted)
          return false;
        active.revision += 1;
        const wasUnread = items.current.some(
          (item) => item.id === id && !item.read
        );
        replaceItems(
          items.current.map((item) =>
            item.id === id ? { ...item, read: true } : item
          )
        );
        if (wasUnread) setUnreadCount((count) => Math.max(0, count - 1));
        return true;
      } catch (err) {
        handleError(err, active, "Failed to mark notification as read.");
        return false;
      } finally {
        active.pending.delete(id);
      }
    },
    [handleError, replaceItems]
  );

  const markAllNotificationsAsRead = useCallback(async () => {
    const active = session.current;
    if (!active || active.pending.has("all")) return false;
    active.pending.add("all");
    const ids = new Set(items.current.map((item) => item.id));
    try {
      // Preserve the existing application's mark-all endpoint.
      await axios.put(
        `${API_BASE}/api/v1/notifications/allread`,
        {},
        active.options
      );
      if (session.current !== active || active.controller.signal.aborted)
        return false;
      active.revision += 1;
      replaceItems(
        items.current.map((item) =>
          ids.has(item.id) ? { ...item, read: true } : item
        )
      );
      await fetchNotifications({ silent: true });
      return true;
    } catch (err) {
      handleError(err, active, "Failed to mark all notifications as read.");
      return false;
    } finally {
      active.pending.delete("all");
    }
  }, [fetchNotifications, handleError, replaceItems]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      unreadDisplayCount: unreadCount > 99 ? "99+" : `${unreadCount}`,
      loading,
      error,
      connectionStatus,
      fetchNotifications,
      retryNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      clearNotifications,
    }),
    [
      notifications,
      unreadCount,
      loading,
      error,
      connectionStatus,
      fetchNotifications,
      retryNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      clearNotifications,
    ]
  );
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
